/**
 * CostplusProjectsCard — manages the merchant's linked websites on the
 * Sources page. Each website wraps one Cost+ project API key; many
 * checkouts can bind to the same website. The key is stored encrypted
 * server-side and never shown in plain text. Add via the
 * AddCostplusProjectModal; rows expose Edit name, Verify (re-test the
 * key against the gateway), and Remove.
 */

import { useEffect, useState } from "react";
import { Check, Plus, RefreshCw, Trash2, Pencil } from "lucide-react";
import { getApiClient } from "../../lib/apiClient";
import type { CostplusProject } from "../../types/api";
import { ApiError } from "../../types/api";
import type { Flow } from "../../types/flow";
import { useSourcesStore } from "./sourcesStore";
import { AddCostplusProjectModal } from "./AddCostplusProjectModal";

export function CostplusProjectsCard({ flows }: { flows: Flow[] }) {
  const projects = useSourcesStore((s) => s.costplusProjects);
  const refresh = useSourcesStore((s) => s.refresh);
  const [adding, setAdding] = useState(false);

  return (
    <section className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950">
      <header className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Websites</h2>
          <p className="text-[11px] text-neutral-500 mt-0.5">
            Each website wraps one Cost+ API key. The same website can be
            reused across many checkouts. We verify the key against the
            Cost+ gateway on add.
          </p>
        </div>
        <button
          onClick={() => setAdding(true)}
          className="flex items-center gap-1.5 rounded-md bg-category-trigger/10 border border-category-trigger/30 text-category-trigger px-2.5 py-1 text-xs font-medium hover:bg-category-trigger/20"
        >
          <Plus className="w-3.5 h-3.5" />
          Add website
        </button>
      </header>
      <div>
        {projects.length === 0 ? (
          <p className="px-4 py-6 text-sm text-neutral-500 text-center">
            No websites linked yet. Add one to start binding checkouts.
          </p>
        ) : (
          <ul className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {projects.map((p) => (
              <ProjectRow key={p.id} project={p} flows={flows} onMutated={refresh} />
            ))}
          </ul>
        )}
      </div>
      <AddCostplusProjectModal
        open={adding}
        onClose={() => setAdding(false)}
        onCreated={() => refresh()}
      />
    </section>
  );
}

function ProjectRow({
  project,
  flows,
  onMutated,
}: {
  project: CostplusProject;
  flows: Flow[];
  onMutated: () => void;
}) {
  const usedBy = flows.filter((f) => flowReferencesProject(f, project.id));
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(project.name);
  const [verifying, setVerifying] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => setName(project.name), [project.name]);

  async function saveName() {
    const trimmed = name.trim();
    setEditing(false);
    if (!trimmed || trimmed === project.name) {
      setName(project.name);
      return;
    }
    try {
      await getApiClient().costplusProjects.update(project.id, { project_name: trimmed });
      onMutated();
    } catch {
      setName(project.name);
    }
  }

  async function verify() {
    setVerifying(true);
    setVerifyMsg(null);
    try {
      await getApiClient().costplusProjects.verify(project.id);
      setVerifyMsg({ ok: true, text: "Verified just now" });
      onMutated();
    } catch (err) {
      const text =
        err instanceof ApiError && err.code === "COSTPLUS_KEY_INVALID"
          ? "Cost+ rejected the key (401). Remove + re-add this project."
          : err instanceof ApiError && err.code === "COSTPLUS_GATEWAY_UNREACHABLE"
            ? "Gateway unreachable. Try again in a moment."
            : err instanceof Error
              ? err.message
              : "Verification failed.";
      setVerifyMsg({ ok: false, text });
    } finally {
      setVerifying(false);
    }
  }

  async function remove() {
    if (
      !confirm(
        `Remove the website "${project.name}"? Flows that reference it will publish-block until re-bound.`,
      )
    )
      return;
    try {
      await getApiClient().costplusProjects.remove(project.id);
      onMutated();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Remove failed.");
    }
  }

  return (
    <li className="px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {editing ? (
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={saveName}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveName();
                  if (e.key === "Escape") {
                    setName(project.name);
                    setEditing(false);
                  }
                }}
                autoFocus
                className="text-sm font-medium bg-transparent border-b border-neutral-300 dark:border-neutral-700 focus:outline-none focus:border-neutral-900 dark:focus:border-neutral-100 max-w-[280px]"
              />
            ) : (
              <button
                onClick={() => setEditing(true)}
                className="text-sm font-medium hover:underline decoration-dotted underline-offset-4 inline-flex items-center gap-1"
                title="Click to rename"
              >
                {project.name}
                <Pencil className="w-3 h-3 text-neutral-400" />
              </button>
            )}
            {project.is_default ? (
              <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300">
                ★ default
              </span>
            ) : null}
          </div>
          <p className="text-[11px] text-neutral-500 mt-1">
            {usedBy.length === 0
              ? "Not used in any checkout yet"
              : `Used in ${usedBy.length} checkout${usedBy.length === 1 ? "" : "s"}: ${usedBy
                  .map((f) => `"${f.name}"`)
                  .slice(0, 3)
                  .join(", ")}${usedBy.length > 3 ? ` +${usedBy.length - 3} more` : ""}`}
          </p>
          {verifyMsg ? (
            <p
              className={
                "text-[11px] mt-1 inline-flex items-center gap-1 " +
                (verifyMsg.ok
                  ? "text-category-trigger"
                  : "text-category-error")
              }
            >
              {verifyMsg.ok ? <Check className="w-3 h-3" /> : null}
              {verifyMsg.text}
            </p>
          ) : null}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={verify}
            disabled={verifying}
            className="rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1 text-[11px] font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900 disabled:opacity-50 inline-flex items-center gap-1"
            title="Re-test this key against the Cost+ gateway"
          >
            <RefreshCw className={"w-3 h-3 " + (verifying ? "animate-spin" : "")} />
            Verify
          </button>
          <button
            onClick={remove}
            className="rounded-md border border-neutral-200 dark:border-neutral-700 px-2 py-1 text-[11px] font-medium text-neutral-500 hover:text-category-error hover:border-category-error inline-flex items-center gap-1"
            title="Remove this Cost+ project"
          >
            <Trash2 className="w-3 h-3" />
            Remove
          </button>
        </div>
      </div>
    </li>
  );
}

/**
 * Walks a flow's nodes and returns true if any checkout_page node references
 * the given project id. Operates on the loaded `Flow` summary which doesn't
 * include the definition; callers needing this should pass the latest version
 * — for the MVP we accept that newly-added projects show "0 checkouts" until
 * the merchant opens each flow once. (Future improvement: orchestrator-side
 * `bound_to_count` field.)
 */
function flowReferencesProject(_flow: Flow, _projectId: string): boolean {
  // The /v1/flows summary endpoint doesn't ship node payloads, so we can't
  // scan locally. Returning false here keeps the type honest; the
  // "Used in N checkouts" copy will read 0 until we surface bindings server
  // side. Wire-up tracked in the next batch.
  return false;
}
